import React from 'react';
import PropTypes from 'prop-types';
import ModelSelection from '@/features/model-setup/components/ModelSelection';
import InfoBanner from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/InfoBanner';
import TextField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/TextField';
import TextAreaField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/TextAreaField';
import FieldLabel from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/FieldLabel';
import AdvancedSettingsSection from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/AdvancedSettingsSection';
import LabelsMultiSelect from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/LabelsMultiSelect';
import DatasetNameLoadRow from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/DatasetNameLoadRow';
import ExcludeDatasetsField from '@/features/datasets/components/DataCollection/RetrievalAgent/steps/ExcludeDatasetsField';
import { GENERATION_GOAL_PLACEHOLDER } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';
import {
    DESCRIPTION_PLACEHOLDER,
    HELP_TEXTS,
    LOAD_PREVIOUS_LABEL,
} from '@/features/datasets/components/DataCollection/RetrievalAgent/constants';

/**
 * Retrieval Agent step 1 (Figma Data Creation): records banner, the Dataset
 * Name + load-previous configurations row, Generation Goal + Description with
 * the Active Labels / Entity types multi-select, then the shared Advanced
 * settings section (with the required Select Provider field) and Exclude
 * Datasets.
 */
const DefineTaskStep = ({
    bannerText,
    datasetName = '',
    onDatasetNameChange,
    datasetNameError = '',
    sessions = [],
    loadedSessionTitle,
    onLoadSession,
    goal,
    goalError = false,
    onGoalChange,
    customGuidelines,
    customGuidelinesError = false,
    onCustomGuidelinesChange,
    labelHeading,
    labelOptions = [],
    labelNames = [],
    onLabelNamesChange,
    domain,
    onDomainChange,
    agentModel,
    agentModelError = false,
    onAgentModelChange,
    languages,
    onLanguagesChange,
    toneStyles,
    onToneStylesChange,
    textLength,
    onTextLengthChange,
    contentType,
    onContentTypeChange,
    datasetsToExclude,
    onDatasetsToExcludeChange,
    excludeFile,
    onExcludeFileChange,
}) => (
    <>
        {bannerText ? <InfoBanner text={bannerText} /> : null}
        {onDatasetNameChange ? (
            <DatasetNameLoadRow
                datasetName={datasetName}
                onDatasetNameChange={onDatasetNameChange}
                datasetNameError={datasetNameError}
                loadLabel={LOAD_PREVIOUS_LABEL}
                sessions={sessions}
                loadedSessionTitle={loadedSessionTitle}
                onLoadSession={onLoadSession}
            />
        ) : null}
        <div className="gdai-section">
            <div className="gdai-section-heading fs-unmask">Define Task</div>
            <TextField
                label="Goal"
                required
                help={HELP_TEXTS.generationGoal}
                placeholder={GENERATION_GOAL_PLACEHOLDER}
                value={goal}
                onChange={onGoalChange}
                error={goalError}
            />
            <TextAreaField
                label="Description"
                required
                help={HELP_TEXTS.customGuidelines}
                placeholder={DESCRIPTION_PLACEHOLDER}
                value={customGuidelines}
                onChange={onCustomGuidelinesChange}
                height={147}
                error={customGuidelinesError}
            />
            {labelHeading ? (
                <LabelsMultiSelect
                    heading={labelHeading}
                    options={labelOptions}
                    selected={labelNames}
                    onChange={onLabelNamesChange}
                />
            ) : null}
        </div>
        <AdvancedSettingsSection
            domain={domain}
            onDomainChange={onDomainChange}
            languages={languages}
            onLanguagesChange={onLanguagesChange}
            toneStyles={toneStyles}
            onToneStylesChange={onToneStylesChange}
            textLength={textLength}
            onTextLengthChange={onTextLengthChange}
            contentType={contentType}
            onContentTypeChange={onContentTypeChange}
            providerField={
                <div className={`gdai-field${agentModelError ? ' gdai-field--error' : ''}`}>
                    <FieldLabel text="Select Provider" required help={HELP_TEXTS.provider} />
                    <ModelSelection
                        labelText=""
                        required={false}
                        showEmptyAlert={false}
                        placeholder="Select"
                        value={
                            agentModel?.name
                                ? { provider: agentModel.provider, model: agentModel.name }
                                : null
                        }
                        onChange={({ provider, model }) =>
                            onAgentModelChange({ name: model, provider, label: model })
                        }
                    />
                </div>
            }
        >
            <ExcludeDatasetsField
                datasetsToExclude={datasetsToExclude}
                onDatasetsToExcludeChange={onDatasetsToExcludeChange}
                excludeFile={excludeFile}
                onExcludeFileChange={onExcludeFileChange}
            />
        </AdvancedSettingsSection>
    </>
);

DefineTaskStep.propTypes = {
    bannerText: PropTypes.node,
    datasetName: PropTypes.string,
    onDatasetNameChange: PropTypes.func,
    datasetNameError: PropTypes.string,
    sessions: PropTypes.arrayOf(PropTypes.object),
    loadedSessionTitle: PropTypes.string,
    onLoadSession: PropTypes.func,
    goal: PropTypes.string.isRequired,
    goalError: PropTypes.bool,
    onGoalChange: PropTypes.func.isRequired,
    customGuidelines: PropTypes.string.isRequired,
    customGuidelinesError: PropTypes.bool,
    onCustomGuidelinesChange: PropTypes.func.isRequired,
    labelHeading: PropTypes.string,
    labelOptions: PropTypes.arrayOf(PropTypes.string),
    labelNames: PropTypes.arrayOf(PropTypes.string),
    onLabelNamesChange: PropTypes.func,
    domain: PropTypes.string,
    onDomainChange: PropTypes.func.isRequired,
    agentModel: PropTypes.object,
    agentModelError: PropTypes.bool,
    onAgentModelChange: PropTypes.func.isRequired,
    languages: PropTypes.arrayOf(PropTypes.string).isRequired,
    onLanguagesChange: PropTypes.func.isRequired,
    toneStyles: PropTypes.arrayOf(PropTypes.string).isRequired,
    onToneStylesChange: PropTypes.func.isRequired,
    textLength: PropTypes.string.isRequired,
    onTextLengthChange: PropTypes.func.isRequired,
    contentType: PropTypes.string.isRequired,
    onContentTypeChange: PropTypes.func.isRequired,
    datasetsToExclude: PropTypes.arrayOf(PropTypes.string).isRequired,
    onDatasetsToExcludeChange: PropTypes.func.isRequired,
    excludeFile: PropTypes.shape({ name: PropTypes.string, size: PropTypes.number }),
    onExcludeFileChange: PropTypes.func.isRequired,
};

export default DefineTaskStep;
